import { useState } from "react";
import {
  Box,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { useInitiativeHistory } from "../hooks/useInitiativeHistory";
import InitiativeChangelogEntry from "./InitiativeChangelogEntry";

export default function InitiativeChangelog({ initiative }) {
  const [showAll, setShowAll] = useState(false);
  const { history, loading, error } = useInitiativeHistory(initiative);

  function groupHistory(history) {
    const groups = [];
    for (const entry of history) {
      const last = groups[groups.length - 1];
      if (
        last &&
        last.changedAt === entry.changedAt &&
        last.changedBy === entry.changedBy
      ) {
        last.entries.push(entry);
      } else {
        groups.push({
          changedAt: entry.changedAt,
          changedBy: entry.changedBy,
          entries: [entry],
        });
      }
    }
    return groups;
  }

  const styles = {
    root: { mb: 3 },
    sectionHeading: { fontWeight: 700, color: "background.text" },
    body: { mt: 1 },
    divider: { my: 1.5 },
    emptyState: { color: "background.textMuted" },
    entryMeta: { color: "background.textMuted", display: "block", mb: 0.5 },
    groupEntries: { display: "flex", flexDirection: "column", gap: 1 },
    showMoreWrap: {
      display: "flex",
      alignItems: "center",
      gap: 1.25,
      mt: 1.5,
    },
    showMoreDivider: {
      flex: 1,
      height: "1px",
      bgcolor: "divider",
    },
    showMoreBtn: {
      cursor: "pointer",
      color: "primary.main",
      userSelect: "none",
      flexShrink: 0,
      "&:hover": { opacity: 0.75 },
    },
  };

  return (
    <Box sx={styles.root}>
      <Typography variant="subtitle2" sx={styles.sectionHeading}>
        Changelog
      </Typography>
      <Box sx={styles.body}>
        {loading && (
          <Typography variant="body2" sx={styles.emptyState}>
            Loading history…
          </Typography>
        )}
        {!loading && error && (
          <Typography variant="body2" sx={styles.emptyState}>
            Failed to load history.
          </Typography>
        )}
        {!loading && !error && history.length === 0 && (
          <Typography variant="body2" sx={styles.emptyState}>
            No history yet.
          </Typography>
        )}
        {!loading && !error && history.length > 0 && (() => {
          const groups = groupHistory(history);
          const visibleGroups = showAll ? groups : groups.slice(0, 2);
          return (
            <>
              <Stack divider={<Divider sx={styles.divider} />}>
                {visibleGroups.map((group) => (
                  <Box key={group.entries[0].id}>
                    <Typography variant="caption" sx={styles.entryMeta}>
                      {group.changedBy} ·{" "}
                      {new Date(group.changedAt).toLocaleString()}
                    </Typography>
                    <Box sx={styles.groupEntries}>
                      {group.entries.map((entry) => (
                        <InitiativeChangelogEntry key={entry.id} entry={entry} />
                      ))}
                    </Box>
                  </Box>
                ))}
              </Stack>
              {groups.length > 2 && (
                <Box sx={styles.showMoreWrap}>
                  <Box sx={styles.showMoreDivider} />
                  <Typography
                    variant="caption"
                    sx={styles.showMoreBtn}
                    onClick={() => setShowAll((prev) => !prev)}
                  >
                    {showAll ? "Show less" : `Show ${groups.length - 2} more`}
                  </Typography>
                  <Box sx={styles.showMoreDivider} />
                </Box>
              )}
            </>
          );
        })()}
      </Box>
    </Box>
  );
}
